import classnames from 'classnames';
import _, { isFinite } from 'lodash';
import {
  IconButton,
  ConfirmModal,
  UnappliedChangesIcon as unappliedChangesIcon,
} from '@/shared/ui';
import { convertToMomentJs } from '@/shared/lib/aiUtils';
import { isValidHr } from '@/entities/heart-rate';
import ButtonAddEcgBookmark from '@/widgets/hr-summary/ui/buttonAddEcgBookmark';
import questionAnswer from './question-icon.svg';
import useDailySummaryThumbnail from './useDailySummaryThumbnail';

export type DailySummaryThumbnailProps = {
  timezoneOffset?: any;
};

/**
 * Renders the left-hand day picker of the HR summary, headed by the report-wide max and min HR events. Combined daily summaries come from useDailySummaryThumbnail; non-finite readings show as NA, days differing from their original values get an unapplied-changes badge, the View buttons jump to that HR event, and the question icon opens a modal explaining the two report-wide figures.
 *
 * @param props - Carries the timezone offset used to format each day's date.
 * @returns The rendered daily summary column with its header, day thumbnails, bookmark button and explanation modal.
 */
const DailySummaryThumbnail = ({ timezoneOffset }: DailySummaryThumbnailProps) => {
  const {
    selectedDateValue,
    dailySummariesCombine,
    isOpenModal,
    setIsOpenModal,
    dataMinMaxHr,
    onClickThumbnail,
    onClickViewMaxHr,
    onClickViewMinHr,
    isValueDifferentFromOrigin,
  } = useDailySummaryThumbnail({ timezoneOffset });

  return (
    <div id="left-column" className="mct-daily-summary-thumbnail-wrapper">
      <div className="mct-daily-summary-thumbnail-header">
        <div className="w80">
          <div className="col1">
            <div className="title">Max HR Event:</div>
            {dataMinMaxHr && dataMinMaxHr.maxHr && isFinite(dataMinMaxHr.maxHr.value) ? (
              <>
                <div className="value">{dataMinMaxHr.maxHr.value}</div>
                <div className="days">(day {dataMinMaxHr.maxHr.days})</div>
                <button
                  id="view-max-hr-btn"
                  className="hrs-view-btn"
                  onClick={() => {
                    onClickViewMaxHr(dataMinMaxHr);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onClickViewMaxHr(dataMinMaxHr);
                    }
                  }}
                >
                  View
                </button>
              </>
            ) : (
              <div className="value">NA</div>
            )}
          </div>
          <div className="col1">
            <div className="title">Min HR Event:</div>
            {dataMinMaxHr && dataMinMaxHr.minHr && isFinite(dataMinMaxHr.minHr.value) ? (
              <>
                <div className="value">{dataMinMaxHr.minHr.value}</div>
                <div className="days">(day {dataMinMaxHr.minHr.days})</div>
                <button
                  id="view-min-hr-btn"
                  className="hrs-view-btn"
                  onClick={() => {
                    onClickViewMinHr(dataMinMaxHr);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      onClickViewMinHr(dataMinMaxHr);
                    }
                  }}
                >
                  View
                </button>
              </>
            ) : (
              <div className="value">NA</div>
            )}
          </div>
        </div>
        <div className="w20">
          <IconButton
            id="question-btn"
            isOutline
            iconComponent={<img src={questionAnswer} alt="Questions icon" />}
            onClick={() => {
              setIsOpenModal(true);
            }}
          />
        </div>
      </div>
      <div className="mct-daily-summary-thumbnail">
        {_.map(dailySummariesCombine, (x: any, i: number) => (
          <button
            id={`summary-thumbnail-${i}`}
            className={classnames(
              'summary-thumbnail-item',
              x.date === selectedDateValue ? '--active' : '',
            )}
            onClick={() => {
              onClickThumbnail(x, i);
            }}
          >
            {isValueDifferentFromOrigin(x) && (
              <img
                src={unappliedChangesIcon}
                alt="Unapplied changes icon"
                className="unapplied-changes-indicator"
              />
            )}
            <div className="date-row --space">
              <div>{convertToMomentJs(x.date, timezoneOffset).format('MM/DD')}</div>
              <div>{`Day ${i + 1}`}</div>
            </div>
            <div className="hr-row">
              <div className="hr-info">
                <div>{isValidHr(x.minHr?.value) ? x.minHr?.value : 'NA'}</div>
                <div>Min</div>
              </div>
              <div className="hr-info">
                <div>{isValidHr(x.avgHr?.value) ? x.avgHr?.value : 'NA'}</div>
                <div>Avg</div>
              </div>
              <div className="hr-info">
                <div>{isValidHr(x.maxHr?.value) ? x.maxHr?.value : 'NA'}</div>
                <div>Max</div>
              </div>
            </div>
          </button>
        ))}
      </div>
      <div className="function-btn">
        <ButtonAddEcgBookmark />
      </div>
      <ConfirmModal
        isOpen={isOpenModal}
        isFilledRightBtn={false}
        isRedBtn={false}
        className="minmaxhr-popup modal-width-350"
        onClickRightButton={() => {
          setIsOpenModal(false);
        }}
        rightButtonName="Got it"
        title="Max/Min HR Event"
        // ConfirmModal's `question` prop is typed as string, but a ReactNode is passed here.
        question={
          (
            <>
              <div className="mb2">
                Max HR Event is the highest maximum HR for the duration of the report.
              </div>
              <div className="mb2">
                Min HR Event is the lowest minimum HR for the duration of the report.
              </div>
            </>
          ) as any
        }
      />
    </div>
  );
};

export default DailySummaryThumbnail;
